import type { DayCount } from '@/api/types'
import { cn } from '@/lib/cn'
import { isToday, monthGridDays, startOfMonth, toIsoDate } from '@/lib/scheduleTime'

const NAGLOWKI = ['Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'So', 'Nd']

interface MonthViewProps {
  anchor: Date
  counts: DayCount[]
  onSelectDay: (day: Date) => void
}

/** Kalendarz miesięczny z liczbą zajęć w każdym dniu. */
export function MonthView({ anchor, counts, onSelectDay }: MonthViewProps) {
  const byDay = new Map(counts.map((entry) => [entry.day, entry]))
  const days = monthGridDays(anchor)
  const currentMonth = startOfMonth(anchor).getMonth()

  return (
    <div className="p-4 sm:p-6">
      <div className="grid grid-cols-7 gap-1 sm:gap-2">
        {NAGLOWKI.map((label) => (
          <div
            key={label}
            className="pb-2 text-center text-[11px] font-bold uppercase tracking-wider text-on-surface-variant"
          >
            {label}
          </div>
        ))}

        {days.map((day) => {
          const entry = byDay.get(toIsoDate(day))
          const planned = entry?.planned ?? 0
          const completed = entry?.completed ?? 0
          const cancelled = entry?.cancelled ?? 0
          const total = planned + completed + cancelled
          const outsideMonth = day.getMonth() !== currentMonth

          return (
            <button
              key={day.toISOString()}
              type="button"
              onClick={() => onSelectDay(day)}
              aria-label={`${day.getDate()} — ${total} zajęć`}
              aria-current={isToday(day) ? 'date' : undefined}
              className={cn(
                'flex min-h-[76px] flex-col items-start gap-1 rounded-lg border p-2 text-left transition-colors sm:min-h-[92px]',
                outsideMonth
                  ? 'border-transparent text-on-surface-variant/40'
                  : 'border-outline/10 hover:border-primary/30 hover:bg-primary/5',
                isToday(day) && 'border-primary bg-primary/5',
              )}
            >
              <span
                className={cn(
                  'text-sm font-bold tabular-nums',
                  isToday(day) && 'text-primary',
                  outsideMonth && 'font-normal',
                )}
              >
                {day.getDate()}
              </span>

              {total > 0 && (
                <span className="flex flex-wrap gap-1">
                  {planned > 0 && (
                    <span className="rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-bold text-on-primary">
                      {planned}
                    </span>
                  )}
                  {completed > 0 && (
                    <span className="rounded-full bg-success-container px-1.5 py-0.5 text-[10px] font-bold text-on-success-container">
                      {completed}
                    </span>
                  )}
                  {cancelled > 0 && (
                    <span className="rounded-full bg-error-container px-1.5 py-0.5 text-[10px] font-bold text-on-error-container">
                      {cancelled}
                    </span>
                  )}
                </span>
              )}
            </button>
          )
        })}
      </div>

      <Legenda />
    </div>
  )
}

/**
 * Legenda kolorów zajęć.
 *
 * `zObecnosciami` dokłada dwa stany, które widać wyłącznie w siatce godzin.
 * Widok miesiąca liczy zajęcia po statusie lekcji i nie zna wpisów obecności,
 * więc pokazywanie tam tych kolorów obiecywałoby rozróżnienie, którego nie ma.
 */
export function Legenda({ zObecnosciami = false }: { zObecnosciami?: boolean }) {
  return (
    <div className="mt-6 flex flex-wrap gap-4 border-t border-outline/10 pt-4">
      <LegendItem className="bg-primary" label="Zaplanowane" />
      <LegendItem className="bg-success-container" label="Odbyte" />
      {zObecnosciami && (
        <>
          <LegendItem className="bg-warning-container" label="Nieobecność zgłoszona" />
          <LegendItem
            className="border-2 border-error bg-error-container"
            label="Nieobecność bez zgłoszenia"
          />
        </>
      )}
      <LegendItem className="bg-error-container" label="Odwołane" />
    </div>
  )
}

function LegendItem({ className, label }: { className: string; label: string }) {
  return (
    <span className="flex items-center gap-2 text-xs font-semibold text-on-surface-variant">
      <span className={cn('h-3 w-3 rounded-full', className)} aria-hidden="true" />
      {label}
    </span>
  )
}

interface YearViewProps {
  year: number
  counts: { year: number; month: number; planned: number; completed: number; cancelled: number }[]
  onSelectMonth: (month: Date) => void
}

const MIESIACE = [
  'Styczeń',
  'Luty',
  'Marzec',
  'Kwiecień',
  'Maj',
  'Czerwiec',
  'Lipiec',
  'Sierpień',
  'Wrzesień',
  'Październik',
  'Listopad',
  'Grudzień',
]

/** Widok roczny — dwanaście kafelków z liczbą zajęć w miesiącu. */
export function YearView({ year, counts, onSelectMonth }: YearViewProps) {
  const byMonth = new Map(counts.map((entry) => [entry.month, entry]))
  const today = new Date()

  return (
    <div className="p-4 sm:p-6">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {MIESIACE.map((label, index) => {
          const month = index + 1
          const entry = byMonth.get(month)
          const total = (entry?.planned ?? 0) + (entry?.completed ?? 0) + (entry?.cancelled ?? 0)
          const isCurrent = today.getFullYear() === year && today.getMonth() === index

          return (
            <button
              key={label}
              type="button"
              onClick={() => onSelectMonth(new Date(year, index, 1))}
              className={cn(
                'rounded-xl border p-4 text-left transition-colors',
                isCurrent
                  ? 'border-primary bg-primary/5'
                  : 'border-outline/10 hover:border-primary/30 hover:bg-primary/5',
              )}
            >
              <p
                className={cn(
                  'font-headline text-base font-bold',
                  isCurrent ? 'text-primary' : 'text-secondary',
                )}
              >
                {label}
              </p>
              <p className="mt-2 font-headline text-3xl font-bold tabular-nums text-on-surface">
                {total}
              </p>
              <p className="text-[11px] font-bold uppercase tracking-wide text-on-surface-variant">
                {total === 1 ? 'zajęcia' : 'zajęć'}
              </p>

              {entry && entry.cancelled > 0 && (
                <p className="mt-2 text-[11px] font-semibold text-error">
                  {entry.cancelled} odwołanych
                </p>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
